import React, { useEffect, useMemo, useState, useCallback, Fragment } from 'react';
import { toast } from 'react-toastify';
import { FiDownload, FiFileText, FiX, FiCheck, FiZap, FiInfo } from 'react-icons/fi';
import { Dialog, Transition } from '@headlessui/react';
import { useApiKeys } from '../context/api-keys';
import { handleApiError } from '../api-client/error_handler';
import axios from 'axios';
import {
  trackExportConfirmCanceled,
  trackExportConfirmOpened,
  trackExportConfirmed,
  trackExportFormatChanged,
  trackExportInitiated,
} from '../analytics';

interface ExportControlsProps {
  fetchRecordsCount: number;
  category: { value: string };
  locality: { value: string };
  generateSearchParams: () => URLSearchParams;
}

interface ExportMetadata {
  message: string;
  export_confirmation_required: boolean;
  total_records: number;
  already_paid_records: number;
  remaining_records_to_charge: number;
  extra_units_to_charge: number;
  remaining_quota: number;
  url?: string;
}

type Scope = 'page' | 'all';
type Format = 'json' | 'csv';

const LS_KEY = 'civapi_export_format';

const ExportControls: React.FC<ExportControlsProps> = ({
  fetchRecordsCount,
  category,
  locality,
  generateSearchParams
}) => {
  const { apiKeys } = useApiKeys();
  const [exportInfo, setExportInfo] = useState<ExportMetadata | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingExport, setPendingExport] = useState<{ scope: Scope; format: Format } | null>(null);
  const [format, setFormat] = useState<Format>('csv');
  const [busy, setBusy] = useState<Scope | null>(null);

  const canExportFetchedResults = fetchRecordsCount > 0;

  // Remember last used format
  useEffect(() => {
    const f = (localStorage.getItem(LS_KEY) as Format) || 'csv';
    setFormat(f);
  }, []);
  useEffect(() => {
    localStorage.setItem(LS_KEY, format);
  }, [format]);

  // Keyboard shortcuts: E = export page, A = export all, Esc = close modal
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target && (e.target as HTMLElement).tagName.match(/INPUT|TEXTAREA|SELECT/)) return;
      if (e.key === 'e' || e.key === 'E') handleExport('page');
      if (e.key === 'a' || e.key === 'A') handleExport('all');
      if (e.key === 'Escape') setIsModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canExportFetchedResults, format, apiKeys]);

  const buildUrl = (scope: Scope, confirm: boolean, fmt: Format) => {
    const baseParams = new URLSearchParams({
      ...Object.fromEntries(generateSearchParams()),
      export: 'true',
      export_scope: scope,
      confirm_export: String(confirm),
      format: fmt
    });
    return `${process.env.REACT_APP_API_BASE_URL}/${category.value}/${locality.value}?${baseParams.toString()}`;
  };

  const downloadFile = (url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = ''; // let server filename win
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const ensureKey = () => {
    const k = apiKeys?.[0]?.api_key;
    if (!k) {
      toast.error('Missing API key.');
      return null;
    }
    return k;
  };

  const handleExport = useCallback(
    async (scope: Scope) => {
      const key = ensureKey();
      if (!key) return;

      if (scope === 'page' && !canExportFetchedResults) {
        toast.error('No data fetched to export');
        return;
      }

      try {
        setBusy(scope);
        trackExportInitiated({
          scope,
          format,
          record_count: fetchRecordsCount,
          category: category.value,
          locality: locality.value,
        });
        const toastId = toast.loading('Preparing export…');

        const { data } = await axios.get<ExportMetadata>(buildUrl(scope, false, format), {
          headers: {
            'x-api-key': key,
            'x-records': String(fetchRecordsCount)
          }
        });

        if (data.export_confirmation_required) {
          setExportInfo(data);
          setPendingExport({ scope, format });
          setIsModalOpen(true);
          trackExportConfirmOpened({ scope, format, record_count: fetchRecordsCount });
          toast.update(toastId, { render: 'Review export details', type: 'info', isLoading: false, autoClose: 2000 });
        } else if (data.url) {
          downloadFile(data.url);
          toast.update(toastId, { render: 'Export complete', type: 'success', isLoading: false, autoClose: 2000 });
        } else {
          toast.update(toastId, {
            render: data.message ? data.message : 'Export failed',
            type: 'error',
            isLoading: false,
            closeButton: true
          });
        }
      } catch (e: any) {
        toast.error(handleApiError(e));
      } finally {
        setBusy(null);
      }
    },
    [format, fetchRecordsCount, apiKeys, category.value, locality.value]
  );

  const confirmExport = useCallback(
    async () => {
      if (!pendingExport) return;
      const key = ensureKey();
      if (!key) return;

      try {
        setBusy(pendingExport.scope);
        trackExportConfirmed({
          scope: pendingExport.scope,
          format: pendingExport.format,
          record_count: fetchRecordsCount,
        });
        const toastId = toast.loading('Exporting…');

        const { data } = await axios.get<ExportMetadata>(buildUrl(pendingExport.scope, true, pendingExport.format), {
          headers: {
            'x-api-key': key,
            'x-records': String(fetchRecordsCount)
          }
        });
        if (data.url) {
          downloadFile(data.url);
          toast.update(toastId, { render: 'Export complete', type: 'success', isLoading: false, autoClose: 2000 });
        } else {
          toast.update(toastId, {
            render: data.message ? data.message : 'Export failed',
            type: 'error',
            isLoading: false,
            closeButton: true
          });
        }
      } catch (e: any) {
        toast.error(handleApiError(e));
      } finally {
        setIsModalOpen(false);
        setExportInfo(null);
        setPendingExport(null);
        setBusy(null);
      }
    },
    [pendingExport, fetchRecordsCount, apiKeys, category.value, locality.value]
  );

  const quotaChip = useMemo(() => {
    if (!exportInfo) return null;
    const remaining = exportInfo.remaining_quota ?? 0;
    const charge = exportInfo.remaining_records_to_charge ?? 0;
    const extras = exportInfo.extra_units_to_charge ?? 0;
    return (
      <div className="hidden sm:flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1 text-xs text-slate-700 ring-1 ring-slate-200">
        <FiInfo className="h-3.5 w-3.5" />
        <span>Quota left: <b>{remaining}</b></span>
        <span className="text-slate-400">•</span>
        <span>Billable now: <b>{charge}</b></span>
        {extras > 0 && (
          <>
            <span className="text-slate-400">•</span>
            <span>Extra units: <b>{extras}</b></span>
          </>
        )}
      </div>
    );
  }, [exportInfo]);

  const Badge: React.FC<{ children: React.ReactNode; tone?: 'blue'|'green'|'slate' }> = ({ children, tone = 'slate' }) => (
    <span className={[
      'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1',
      tone === 'blue' ? 'bg-blue-50 text-blue-800 ring-blue-200'
      : tone === 'green' ? 'bg-emerald-50 text-emerald-800 ring-emerald-200'
      : 'bg-slate-100 text-slate-700 ring-slate-200'
    ].join(' ')}>{children}</span>
  );

  return (
    <div className="mt-6 mb-6 flex flex-wrap items-center gap-4">
      <label className="text-sm font-medium text-gray-700">Export:</label>

      <select
        value={format}
        onChange={(e) => {
          const next = e.target.value as Format;
          setFormat(next);
          trackExportFormatChanged({ format: next });
        }}
        className="w-32 rounded border px-2 py-1 text-sm"
        aria-label="Export format"
      >
        <option value="csv">CSV</option>
        <option value="json">JSON</option>
      </select>

      <div className="mt-2 flex items-center gap-6">
        {/* Export Current Page */}
        <div className="flex flex-col items-center">
          <button
            onClick={() => handleExport('page')}
            disabled={!canExportFetchedResults || busy !== null}
            className={[
              'p-2 rounded-full transition',
              !canExportFetchedResults || busy !== null ? 'bg-blue-50 text-blue-300 cursor-not-allowed' : 'bg-blue-100 hover:bg-blue-200 text-blue-700'
            ].join(' ')}
            title={canExportFetchedResults ? 'Export current page (E)' : 'No data to export'}
            aria-busy={busy === 'page'}
          >
            {busy === 'page'
              ? <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
                  <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" />
                </svg>
              : <FiDownload className="h-5 w-5" />}
          </button>
          <span className="mt-1 text-xs text-gray-600">Current Page</span>
        </div>

        {/* Export All */}
        <div className="flex flex-col items-center">
          <button
            onClick={() => handleExport('all')}
            disabled={busy !== null}
            className={[
              'p-2 rounded-full transition',
              busy !== null ? 'bg-green-50 text-green-300 cursor-not-allowed' : 'bg-green-100 hover:bg-green-200 text-green-700'
            ].join(' ')}
            title="Export all data (A)"
            aria-busy={busy === 'all'}
          >
            {busy === 'all'
              ? <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
                  <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" />
                </svg>
              : <FiFileText className="h-5 w-5" />}
          </button>
          <span className="mt-1 text-xs text-gray-600">All Data</span>
        </div>

        {/* quick hint */}
        <div className="hidden md:flex items-center gap-2 text-xs text-slate-500">
          <FiZap className="h-4 w-4" />
          <span>Shortcuts: <kbd className="rounded border px-1">E</kbd> page · <kbd className="rounded border px-1">A</kbd> all</span>
        </div>
      </div>

      {/* Live quota chip (when API asks confirmation) */}
      {quotaChip}

      {/* Confirmation Modal */}
      <Transition show={isModalOpen && !!exportInfo} as={Fragment}>
        <Dialog
          onClose={() => {
            if (pendingExport) {
              trackExportConfirmCanceled({
                scope: pendingExport.scope,
                format: pendingExport.format,
              });
            }
            setIsModalOpen(false);
          }}
          className="relative z-[9999]"
        >
          <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-120" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
              <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-120" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-2xl ring-1 ring-slate-100">
                  <Dialog.Title className="mb-1 text-base font-semibold text-slate-900">Confirm Export</Dialog.Title>
                  {pendingExport && (
                    <div className="mb-3 flex items-center gap-2">
                      <Badge tone="blue">Scope: {pendingExport.scope === 'all' ? 'All Data' : 'Current Page'}</Badge>
                      <Badge tone="green">Format: {pendingExport.format.toUpperCase()}</Badge>
                    </div>
                  )}
                  {exportInfo?.message && (
                    <p className="mb-3 text-sm text-slate-600">{exportInfo.message}</p>
                  )}
                  <ul className="mb-4 space-y-1 text-sm text-slate-700">
                    <li><strong>Total:</strong> {exportInfo?.total_records}</li>
                    <li><strong>Paid:</strong> {exportInfo?.already_paid_records}</li>
                    <li><strong>Remaining billable:</strong> {exportInfo?.remaining_records_to_charge}</li>
                    <li><strong>Extra units:</strong> {exportInfo?.extra_units_to_charge}</li>
                    <li><strong>Quota left:</strong> {exportInfo?.remaining_quota}</li>
                  </ul>
                  <div className="mt-2 flex justify-end gap-2">
                    <button
                      onClick={() => {
                        if (pendingExport) {
                          trackExportConfirmCanceled({
                            scope: pendingExport.scope,
                            format: pendingExport.format,
                          });
                        }
                        setIsModalOpen(false);
                      }}
                      className="rounded-full bg-gray-100 p-2 text-gray-700 hover:bg-gray-200"
                      title="Cancel"
                    >
                      <FiX className="h-4 w-4" />
                    </button>
                    <button
                      onClick={confirmExport}
                      className="rounded-full bg-blue-600 p-2 text-white hover:bg-blue-700"
                      title="Confirm"
                    >
                      <FiCheck className="h-4 w-4" />
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default ExportControls;
